'use client';

import Image, {
  type ImageLoader,
  type ImageProps,
  getImageProps,
} from 'next/image';

type UmbracoImageProps = Omit<ImageProps, 'loader' | 'src'> & {
  src: string;
  largeImageSrc?: string | null;
  largeImageBreakpoint?: string | null;
  largeImageWidth?: number | null;
  largeImageHeight?: number | null;
};

const isUnoptimised = (imgSrc?: string | null) => {
  switch (true) {
    case imgSrc?.endsWith('.gif'):
    case imgSrc?.endsWith('.svg'):
      return true;
    default:
      return false;
  }
};

export const UmbracoImage = ({
  src,
  width,
  height,
  largeImageSrc,
  largeImageWidth,
  largeImageHeight,
  largeImageBreakpoint,
  className,
  alt,
  ...props
}: UmbracoImageProps) => {
  const umbracoImageLoader: ImageLoader = ({ src, width, quality }) =>
    `${src}?w=${width}&q=${quality ?? 75}`;

  const imageSrcIsUnoptimised = isUnoptimised(src);
  const largeImageSrcIsUnoptimised = isUnoptimised(largeImageSrc);

  if (!largeImageSrc) {
    return (
      <Image
        data-optimised={!imageSrcIsUnoptimised}
        unoptimized={imageSrcIsUnoptimised}
        alt={alt}
        className={className}
        loader={umbracoImageLoader}
        src={src}
        width={width}
        height={height}
        {...props}
      />
    );
  }

  const {
    props: { srcSet: mobile, ...rest },
  } = getImageProps({
    ...props,
    alt,
    width,
    height,
    loader: umbracoImageLoader,
    unoptimized: imageSrcIsUnoptimised,
    src: src,
  });

  const {
    props: { srcSet: desktop },
  } = getImageProps({
    ...props,
    alt,
    width: largeImageWidth ?? undefined,
    height: largeImageHeight ?? undefined,
    loader: umbracoImageLoader,
    unoptimized: largeImageSrcIsUnoptimised,
    src: largeImageSrc,
  });

  const breakpointSize = getBreakpointSize(largeImageBreakpoint);

  return (
    <picture>
      <source
        data-optimised={!largeImageSrcIsUnoptimised}
        media={`(min-width: ${breakpointSize}px)`}
        srcSet={desktop}
        width={largeImageWidth ?? undefined}
        height={largeImageHeight ?? undefined}
      />
      <source
        data-optimised={!imageSrcIsUnoptimised}
        media={`(max-width: ${breakpointSize - 1}px)`}
        srcSet={mobile}
      />
      <img
        {...rest}
        style={{ width: '100%', height: 'auto' }}
        alt={alt}
        // sizes={tailwindBreakpointSizesToImageSizes({}, '100vw')} // TODO: Work out how to handle sizes where needed
      />
    </picture>
  );
};

const getBreakpointSize = (breakpoint?: string | null) => {
  switch (breakpoint) {
    case 'sm':
      return 640;
    case 'lg':
      return 1024;
    case 'xl':
      return 1280;
    case '2xl':
      return 1536;
    case 'md': // default to md
    default:
      return 768;
  }
};
